import type {
  BetAction,
  LoveWinsAllAction,
  LoveWinsAllPlayerId,
  LoveWinsAllState,
  RoundResult,
} from "../types";
import {
  HAND_SIZE,
  LOVE_WINS_ALL_ANTE,
  LOVE_WINS_ALL_BET_UNIT,
  RESHUFFLE_THRESHOLD,
} from "../types";
import {
  chipWinner,
  compareRoundHands,
  fullDeck,
  getOpponentPlayerId,
  handRank,
  outstandingToCall,
  shuffleDeck,
} from "../utils";

/**
 * Begin a round: reshuffle if the draw pile is short, deal 3 cards to each
 * player, pay antes, and open the betting. Resolves to game-over if a player
 * cannot ante. Does NOT change moveNumber — it continues the action that
 * triggered it (init / settle).
 */
export function beginRound(state: LoveWinsAllState): LoveWinsAllState {
  const lead = state.leadPlayerId;
  const follower = getOpponentPlayerId(lead);

  if (state.chips[lead] < LOVE_WINS_ALL_ANTE) {
    return { ...state, phase: "game-over", winnerId: follower };
  }
  if (state.chips[follower] < LOVE_WINS_ALL_ANTE) {
    return { ...state, phase: "game-over", winnerId: lead };
  }

  let deck = state.deck;
  let rngState = state.rngState;
  if (deck.length < RESHUFFLE_THRESHOLD) {
    const shuffled = shuffleDeck(fullDeck(), rngState);
    deck = shuffled.deck;
    rngState = shuffled.state;
  }
  const handP1 = deck.slice(0, HAND_SIZE);
  const handP2 = deck.slice(HAND_SIZE, HAND_SIZE * 2);
  deck = deck.slice(HAND_SIZE * 2);

  return {
    ...state,
    phase: "bet",
    currentPlayerId: lead,
    deck,
    rngState,
    hands: { p1: handP1, p2: handP2 },
    chips: {
      p1: state.chips.p1 - LOVE_WINS_ALL_ANTE,
      p2: state.chips.p2 - LOVE_WINS_ALL_ANTE,
    },
    pot: 2 * LOVE_WINS_ALL_ANTE,
    contributed: { p1: LOVE_WINS_ALL_ANTE, p2: LOVE_WINS_ALL_ANTE },
    raisesUsed: 0,
    lastAggressorId: null,
  };
}

/** Settle the round (showdown or fold), then advance to the next round or end. */
function settleRound(
  state: LoveWinsAllState,
  folderId: LoveWinsAllPlayerId | null,
): LoveWinsAllState {
  const winnerId =
    folderId !== null
      ? getOpponentPlayerId(folderId)
      : compareRoundHands(state);

  const chips =
    winnerId === "push"
      ? {
          p1: state.chips.p1 + state.contributed.p1,
          p2: state.chips.p2 + state.contributed.p2,
        }
      : { ...state.chips, [winnerId]: state.chips[winnerId] + state.pot };

  const lastRound: RoundResult = {
    round: state.round,
    leadPlayerId: state.leadPlayerId,
    hands: { p1: state.hands.p1, p2: state.hands.p2 },
    ranks: { p1: handRank(state.hands.p1), p2: handRank(state.hands.p2) },
    folderId,
    winnerId,
    pot: state.pot,
  };

  const settled: LoveWinsAllState = {
    ...state,
    chips,
    pot: 0,
    contributed: { p1: 0, p2: 0 },
    raisesUsed: 0,
    lastAggressorId: null,
    lastRound,
    moveNumber: state.moveNumber + 1,
  };

  const gameWinner = chipWinner(chips);
  if (gameWinner !== null) {
    return { ...settled, phase: "game-over", winnerId: gameWinner };
  }

  // Lead passes to the round winner; on a push the lead is unchanged.
  const nextLead = winnerId === "push" ? state.leadPlayerId : winnerId;
  return beginRound({
    ...settled,
    round: state.round + 1,
    leadPlayerId: nextLead,
    currentPlayerId: nextLead,
  });
}

export function applyBet(
  state: LoveWinsAllState,
  action: BetAction,
): LoveWinsAllState {
  const actor = action.playerId;
  const opponent = getOpponentPlayerId(actor);
  const base: LoveWinsAllState = { ...state, lastAction: action };

  switch (action.move) {
    case "check":
      // Lead's first check passes to the follower; the follower's check
      // (after the lead checked) closes betting → showdown.
      return actor === state.leadPlayerId
        ? {
            ...base,
            currentPlayerId: opponent,
            moveNumber: state.moveNumber + 1,
          }
        : settleRound(base, null);
    case "bet":
      return {
        ...base,
        chips: {
          ...state.chips,
          [actor]: state.chips[actor] - LOVE_WINS_ALL_BET_UNIT,
        },
        pot: state.pot + LOVE_WINS_ALL_BET_UNIT,
        contributed: {
          ...state.contributed,
          [actor]: state.contributed[actor] + LOVE_WINS_ALL_BET_UNIT,
        },
        lastAggressorId: actor,
        currentPlayerId: opponent,
        moveNumber: state.moveNumber + 1,
      };
    case "raise": {
      const amount = outstandingToCall(state) + LOVE_WINS_ALL_BET_UNIT;
      return {
        ...base,
        chips: { ...state.chips, [actor]: state.chips[actor] - amount },
        pot: state.pot + amount,
        contributed: {
          ...state.contributed,
          [actor]: state.contributed[actor] + amount,
        },
        raisesUsed: state.raisesUsed + 1,
        lastAggressorId: actor,
        currentPlayerId: opponent,
        moveNumber: state.moveNumber + 1,
      };
    }
    case "call": {
      const toCall = outstandingToCall(state);
      const called: LoveWinsAllState = {
        ...base,
        chips: { ...state.chips, [actor]: state.chips[actor] - toCall },
        pot: state.pot + toCall,
        contributed: {
          ...state.contributed,
          [actor]: state.contributed[actor] + toCall,
        },
      };
      return settleRound(called, null);
    }
    case "fold":
      return settleRound(base, actor);
  }
}

export function applyAction(
  state: LoveWinsAllState,
  action: LoveWinsAllAction,
): LoveWinsAllState {
  switch (action.type) {
    case "Bet":
      return applyBet(state, action);
  }
}
